// src/services/advancedTools.service.ts
import { 
  Connection, 
  PublicKey, 
  Transaction,
  SystemProgram,
  LAMPORTS_PER_SOL,
  Keypair,
  sendAndConfirmTransaction
} from '@solana/web3.js';
import { 
  createTransferInstruction,
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID
} from '@solana/spl-token';
import { SOLANA_RPC_ENDPOINT } from '../utils/constants';

interface BundlerWallet {
  keypair: Keypair;
  publicKey: PublicKey;
  balance: number;
  funded: boolean;
  active: boolean;
}

interface SniperTarget {
  tokenAddress: string;
  targetPrice?: number;
  maxBuy: number;
  bought: boolean;
  txSignature?: string;
}

class AdvancedToolsService {
  private connection: Connection;
  private bundlerWallets: Map<string, BundlerWallet> = new Map();
  private sniperTargets: Map<string, SniperTarget> = new Map();
  private sniperInterval: NodeJS.Timeout | null = null;
  private isSnipingActive: boolean = false;

  constructor() {
    this.connection = new Connection(SOLANA_RPC_ENDPOINT, 'confirmed');
  }

  // ==================== BUNDLER FUNCTIONS ====================

  /**
   * Deploy bundler with multiple wallets
   */
  async deployBundler(
    mainWallet: PublicKey,
    walletCount: number,
    amountPerWallet: number,
    delay: number,
    stealthMode: boolean,
    signTransaction: (tx: Transaction) => Promise<Transaction>
  ): Promise<{
    success: boolean;
    wallets: string[];
    totalCost: number;
    error?: string;
  }> {
    try {
      console.log('🚀 Starting bundler deployment...');
      
      // Calculate total cost
      const bundlerFee = 0.02; // Platform fee
      const stealthFee = stealthMode ? 0.05 : 0;
      const totalFunding = walletCount * amountPerWallet;
      const totalCost = totalFunding + bundlerFee + stealthFee;

      // Check main wallet balance
      const balance = await this.connection.getBalance(mainWallet);
      if (balance < totalCost * LAMPORTS_PER_SOL) {
        throw new Error(`Insufficient balance. Need ${totalCost} SOL, have ${balance / LAMPORTS_PER_SOL} SOL`);
      }

      const createdWallets: string[] = [];
      const walletBatch: BundlerWallet[] = [];

      // Step 1: Generate wallets
      console.log(`📱 Generating ${walletCount} wallets...`);
      for (let i = 0; i < walletCount; i++) {
        const keypair = Keypair.generate();
        const wallet: BundlerWallet = {
          keypair,
          publicKey: keypair.publicKey,
          balance: 0,
          funded: false,
          active: false
        };
        
        walletBatch.push(wallet);
        this.bundlerWallets.set(keypair.publicKey.toString(), wallet);
        createdWallets.push(keypair.publicKey.toString());
      }

      // Step 2: Fund wallets with delay for stealth mode
      console.log('💰 Funding wallets...');
      for (let i = 0; i < walletBatch.length; i++) {
        const wallet = walletBatch[i];
        
        try {
          // Create funding transaction
          const transaction = new Transaction().add(
            SystemProgram.transfer({
              fromPubkey: mainWallet,
              toPubkey: wallet.publicKey,
              lamports: amountPerWallet * LAMPORTS_PER_SOL,
            })
          );

          // Get recent blockhash
          const { blockhash } = await this.connection.getLatestBlockhash();
          transaction.recentBlockhash = blockhash;
          transaction.feePayer = mainWallet;

          // Sign and send transaction
          const signedTx = await signTransaction(transaction);
          const signature = await this.connection.sendRawTransaction(signedTx.serialize());
          
          // Wait for confirmation
          await this.connection.confirmTransaction(signature);
          
          wallet.balance = amountPerWallet;
          wallet.funded = true;
          wallet.active = true;
          
          console.log(`✅ Wallet ${i + 1}/${walletCount} funded: ${wallet.publicKey.toString().slice(0, 8)}...`);
          
          // Apply delay if in stealth mode
          if (stealthMode && i < walletBatch.length - 1) {
            const randomDelay = delay * 1000 * (0.5 + Math.random()); // Random delay variation
            await new Promise(resolve => setTimeout(resolve, randomDelay));
          }
        } catch (error) {
          console.error(`❌ Failed to fund wallet ${i + 1}:`, error);
        }
      }

      // Store bundler configuration
      this.saveBundlerConfig({
        mainWallet: mainWallet.toString(),
        wallets: createdWallets,
        amountPerWallet,
        stealthMode,
        createdAt: new Date().toISOString()
      });

      // Save wallets to localStorage for persistence
      this.saveBundlerWallets();

      const fundedCount = walletBatch.filter(w => w.funded).length;
      console.log(`✅ Bundler deployed: ${fundedCount}/${walletCount} wallets funded`);

      return {
        success: fundedCount > 0,
        wallets: createdWallets,
        totalCost,
        error: fundedCount === 0 ? 'Failed to fund any wallets' : undefined
      };

    } catch (error: any) {
      console.error('❌ Bundler deployment failed:', error);
      return {
        success: false,
        wallets: [],
        totalCost: 0,
        error: error.message
      };
    }
  }

  /**
   * Execute coordinated token purchase with bundler wallets
   */
  async executeBundlerPurchase(
    tokenMint: PublicKey,
    amountPerWallet: number,
    delay: number = 2
  ): Promise<{
    success: boolean;
    purchases: number;
    totalSpent: number;
    signatures: string[];
  }> {
    const signatures: string[] = [];
    let purchases = 0;
    let totalSpent = 0;

    try {
      console.log('🎯 Executing bundler purchases...');
      
      // Get active bundler wallets
      const activeWallets = Array.from(this.bundlerWallets.values())
        .filter(w => w.active && w.funded);

      if (activeWallets.length === 0) {
        throw new Error('No active bundler wallets found');
      }

      // Execute purchases with delays
      for (const wallet of activeWallets) {
        try {
          // Create token account for the wallet if it doesn't exist
          const tokenAccount = await getAssociatedTokenAddress(
            tokenMint,
            wallet.publicKey
          );

          // Check if token account exists
          const accountInfo = await this.connection.getAccountInfo(tokenAccount);
          
          if (!accountInfo) {
            // Create associated token account
            const createAtaIx = createAssociatedTokenAccountInstruction(
              wallet.publicKey, // payer
              tokenAccount, // ata
              wallet.publicKey, // owner
              tokenMint // mint
            );

            const transaction = new Transaction().add(createAtaIx);
            const { blockhash } = await this.connection.getLatestBlockhash();
            transaction.recentBlockhash = blockhash;
            transaction.feePayer = wallet.publicKey;

            // Sign with wallet's keypair
            transaction.sign(wallet.keypair);
            
            const sig = await this.connection.sendRawTransaction(transaction.serialize());
            await this.connection.confirmTransaction(sig);
          }

          // Here you would implement the actual token purchase logic
          // This would typically involve interacting with a DEX like Raydium or Jupiter
          // For demo purposes, we'll simulate the purchase
          
          console.log(`✅ Wallet ${wallet.publicKey.toString().slice(0, 8)}... purchased tokens`);
          
          purchases++;
          totalSpent += amountPerWallet;
          signatures.push('simulated_tx_' + wallet.publicKey.toString().slice(0, 8));
          
          // Update wallet balance
          wallet.balance -= amountPerWallet;
          
          // Apply delay between purchases
          if (delay > 0) {
            await new Promise(resolve => setTimeout(resolve, delay * 1000));
          }
          
        } catch (error) {
          console.error(`Failed to purchase with wallet ${wallet.publicKey.toString().slice(0, 8)}:`, error);
        }
      }

      return {
        success: purchases > 0,
        purchases,
        totalSpent,
        signatures
      };

    } catch (error: any) {
      console.error('❌ Bundler purchase failed:', error);
      return {
        success: false,
        purchases: 0,
        totalSpent: 0,
        signatures: []
      };
    }
  }

  /**
   * Withdraw funds from bundler wallets back to main wallet
   */
  async withdrawBundlerFunds(
    mainWallet: PublicKey,
    signTransaction: (tx: Transaction) => Promise<Transaction>
  ): Promise<{
    success: boolean;
    totalWithdrawn: number;
    walletsWithdrawn: number;
  }> {
    let totalWithdrawn = 0;
    let walletsWithdrawn = 0;

    try {
      const activeWallets = Array.from(this.bundlerWallets.values())
        .filter(w => w.balance > 0);

      for (const wallet of activeWallets) {
        try {
          const balance = await this.connection.getBalance(wallet.publicKey);
          
          // Leave minimum for rent
          const withdrawAmount = balance - 5000; // Leave 0.000005 SOL for rent
          
          if (withdrawAmount > 0) {
            const transaction = new Transaction().add(
              SystemProgram.transfer({
                fromPubkey: wallet.publicKey,
                toPubkey: mainWallet,
                lamports: withdrawAmount,
              })
            );

            const { blockhash } = await this.connection.getLatestBlockhash();
            transaction.recentBlockhash = blockhash;
            transaction.feePayer = wallet.publicKey;
            
            // Sign with bundler wallet's keypair
            transaction.sign(wallet.keypair);
            
            const signature = await this.connection.sendRawTransaction(transaction.serialize());
            await this.connection.confirmTransaction(signature);
            
            totalWithdrawn += withdrawAmount / LAMPORTS_PER_SOL;
            walletsWithdrawn++;
            
            wallet.balance = 0;
            wallet.active = false;
          }
        } catch (error) {
          console.error(`Failed to withdraw from wallet:`, error);
        }
      }

      return {
        success: walletsWithdrawn > 0,
        totalWithdrawn,
        walletsWithdrawn
      };

    } catch (error: any) {
      console.error('❌ Withdrawal failed:', error);
      return {
        success: false,
        totalWithdrawn: 0,
        walletsWithdrawn: 0
      };
    }
  }

  // ==================== SNIPER BOT FUNCTIONS ====================

  /**
   * Activate sniper bot
   */
  async activateSniper(
    mainWallet: PublicKey,
    amount: number,
    slippage: number,
    signTransaction: (tx: Transaction) => Promise<Transaction>
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      if (this.isSnipingActive) {
        return {
          success: false,
          message: 'Sniper bot is already active'
        };
      }

      console.log('🎯 Activating sniper bot...');
      this.isSnipingActive = true;

      // Start monitoring for new tokens
      this.startTokenMonitoring(mainWallet, amount, slippage, signTransaction);

      return {
        success: true,
        message: 'Sniper bot activated successfully'
      };

    } catch (error: any) {
      console.error('❌ Failed to activate sniper:', error);
      return {
        success: false,
        message: error.message
      };
    }
  }

  /**
   * Monitor for new token launches
   */
  private startTokenMonitoring(
    mainWallet: PublicKey,
    amount: number,
    slippage: number,
    signTransaction: (tx: Transaction) => Promise<Transaction>
  ) {
    // Monitor for new tokens every 5 seconds
    this.sniperInterval = setInterval(async () => {
      try {
        // In a real implementation, you would:
        // 1. Monitor Raydium/Jupiter for new pools
        // 2. Check token metrics (liquidity, holders, etc.)
        // 3. Execute buy if conditions are met
        
        // For demo, we'll simulate monitoring
        console.log('👀 Scanning for new tokens...');
        
        // Check if we should snipe (demo logic)
        const shouldSnipe = Math.random() > 0.95; // 5% chance per check
        
        if (shouldSnipe) {
          console.log('🎯 New token detected! Attempting to snipe...');
          
          // Simulate snipe attempt
          const fakeTokenAddress = Keypair.generate().publicKey.toString();
          
          this.sniperTargets.set(fakeTokenAddress, {
            tokenAddress: fakeTokenAddress,
            maxBuy: amount,
            bought: true,
            txSignature: 'simulated_snipe_' + Date.now()
          });
          
          console.log(`✅ Successfully sniped token: ${fakeTokenAddress.slice(0, 8)}...`);
        }
        
      } catch (error) {
        console.error('Error during token monitoring:', error);
      }
    }, 5000); // Check every 5 seconds
  }

  /**
   * Add specific token to sniper targets
   */
  async addSniperTarget(
    tokenAddress: string,
    maxBuy: number,
    targetPrice?: number
  ): Promise<boolean> {
    try {
      const publicKey = new PublicKey(tokenAddress);
      
      this.sniperTargets.set(tokenAddress, {
        tokenAddress,
        targetPrice,
        maxBuy,
        bought: false
      });
      
      console.log(`🎯 Added sniper target: ${tokenAddress.slice(0, 8)}...`);
      return true;
      
    } catch (error) {
      console.error('Failed to add sniper target:', error);
      return false;
    }
  }

  /**
   * Stop sniper bot
   */
  stopSniper(): void {
    if (this.sniperInterval) {
      clearInterval(this.sniperInterval);
      this.sniperInterval = null;
    }
    this.isSnipingActive = false;
    console.log('🛑 Sniper bot stopped');
  }

  /**
   * Get sniper status
   */
  getSniperStatus(): {
    active: boolean;
    monitoring: number;
    sniped: number;
    totalSpent: number;
  } {
    const targets = Array.from(this.sniperTargets.values());
    const sniped = targets.filter(t => t.bought).length;
    const totalSpent = targets
      .filter(t => t.bought)
      .reduce((sum, t) => sum + t.maxBuy, 0);

    return {
      active: this.isSnipingActive,
      monitoring: this.sniperTargets.size,
      sniped,
      totalSpent
    };
  }

  // ==================== HELPER FUNCTIONS ====================

  /**
   * Save bundler configuration to localStorage
   */
  private saveBundlerConfig(config: any): void {
    const configs = JSON.parse(localStorage.getItem('bundler_configs') || '[]');
    configs.push(config);
    localStorage.setItem('bundler_configs', JSON.stringify(configs));
  }

  /**
   * Get bundler statistics
   */
  getBundlerStats(): {
    totalWallets: number;
    activeWallets: number;
    totalBalance: number;
    deployments: number;
  } {
    const wallets = Array.from(this.bundlerWallets.values());
    const configs = JSON.parse(localStorage.getItem('bundler_configs') || '[]');
    
    return {
      totalWallets: wallets.length,
      activeWallets: wallets.filter(w => w.active).length,
      totalBalance: wallets.reduce((sum, w) => sum + w.balance, 0),
      deployments: configs.length
    };
  }

  /**
 * Get bundler wallets with keypairs for Jupiter transactions
 * WARNING: This exposes private keys - use with extreme caution!
 */
getBundlerWalletsWithKeypairs(): Array<{
  keypair: Keypair;
  publicKey: PublicKey;
  balance: number;
  funded: boolean;
  active: boolean;
}> {
  const wallets: Array<{
    keypair: Keypair;
    publicKey: PublicKey;
    balance: number;
    funded: boolean;
    active: boolean;
  }> = [];

  // First try to get from memory
  this.bundlerWallets.forEach((wallet) => {
    wallets.push({
      keypair: wallet.keypair,
      publicKey: wallet.publicKey,
      balance: wallet.balance,
      funded: wallet.funded,
      active: wallet.active
    });
  });

  // If empty, try to load from localStorage
  if (wallets.length === 0) {
    try {
      const stored = localStorage.getItem('bundler_wallets_DEV');
      if (stored) {
        const walletsData = JSON.parse(stored);
        
        walletsData.forEach((data: any) => {
          const secretKey = new Uint8Array(data.secretKey);
          const keypair = Keypair.fromSecretKey(secretKey);
          
          wallets.push({
            keypair,
            publicKey: keypair.publicKey,
            balance: data.balance || 0,
            funded: data.funded || false,
            active: data.active || false
          });
          
          // Also restore to memory
          this.bundlerWallets.set(data.address, {
            keypair,
            publicKey: keypair.publicKey,
            balance: data.balance || 0,
            funded: data.funded || false,
            active: data.active || false
          });
        });
      }
    } catch (error) {
      console.error('Failed to load bundler wallets:', error);
    }
  }

  return wallets;
}

/**
 * Get specific bundler wallets by addresses
 */
getBundlerWalletsByAddresses(addresses: string[]): Array<{
  keypair: Keypair;
  publicKey: PublicKey;
  balance: number;
}> {
  const allWallets = this.getBundlerWalletsWithKeypairs();
  
  return addresses
    .map(address => {
      const wallet = allWallets.find(
        w => w.publicKey.toString() === address
      );
      
      if (wallet) {
        return {
          keypair: wallet.keypair,
          publicKey: wallet.publicKey,
          balance: wallet.balance
        };
      }
      return null;
    })
    .filter(Boolean) as Array<{
      keypair: Keypair;
      publicKey: PublicKey;
      balance: number;
    }>;
}

  /**
   * Clear all bundler wallets
   */
  clearBundlerWallets(): void {
    this.bundlerWallets.clear();
    console.log('🗑️ Bundler wallets cleared');
  }

  /**
   * Save bundler wallets to localStorage (DEVELOPMENT ONLY!)
   */
  private saveBundlerWallets(): void {
    const walletsData = Array.from(this.bundlerWallets.entries()).map(([address, wallet]) => ({
      address,
      secretKey: Array.from(wallet.keypair.secretKey),
      balance: wallet.balance,
      funded: wallet.funded,
      active: wallet.active
    }));
    
    localStorage.setItem('bundler_wallets_DEV', JSON.stringify(walletsData));
    console.warn('⚠️ Private keys stored in localStorage - FOR DEVELOPMENT ONLY!');
  }

  /**
   * Load bundler wallets from localStorage
   */
  loadBundlerWallets(): void {
    try {
      const stored = localStorage.getItem('bundler_wallets_DEV');
      if (stored) {
        const walletsData = JSON.parse(stored);
        this.bundlerWallets.clear();
        
        walletsData.forEach((data: any) => {
          const secretKey = new Uint8Array(data.secretKey);
          const keypair = Keypair.fromSecretKey(secretKey);
          
          this.bundlerWallets.set(data.address, {
            keypair,
            publicKey: keypair.publicKey,
            balance: data.balance,
            funded: data.funded,
            active: data.active
          });
        });
        
        console.log(`✅ Loaded ${this.bundlerWallets.size} bundler wallets from storage`);
      }
    } catch (error) {
      console.error('Failed to load bundler wallets:', error);
    }
  }

  /**
   * Export bundler wallets as JSON
   */
  exportBundlerWallets(): string {
    const wallets = Array.from(this.bundlerWallets.entries()).map(([address, wallet]) => ({
      address,
      publicKey: wallet.publicKey.toString(),
      secretKey: Array.from(wallet.keypair.secretKey),
      balance: wallet.balance,
      funded: wallet.funded,
      active: wallet.active,
      warning: 'KEEP THIS SECURE! These are real private keys!'
    }));
    
    return JSON.stringify({
      wallets,
      createdAt: new Date().toISOString(),
      totalWallets: wallets.length,
      warning: '⚠️ CRITICAL: This file contains private keys! Keep it secure and never share it!'
    }, null, 2);
  }

 /**
 * Get wallet details for display
 * Returns full privateKey (hex) and a truncated preview for UI.
 */
getBundlerWalletDetails(): Array<{
  address: string;
  balance: number;
  funded: boolean;
  active: boolean;
  privateKey: string;         // FULL key as hex (for copying)
  privateKeyPreview: string;  // SLICED preview (for display)
}> {
  return Array.from(this.bundlerWallets.entries()).map(([address, wallet]) => {
    // Convert Uint8Array secretKey to hex string (64 bytes for ed25519)
    const secretKeyHex = Array.from(wallet.keypair.secretKey)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    // preview: first 6 ... last 6 (tweak to taste)
    const privateKeyPreview = `${secretKeyHex.slice(0, 6)}...${secretKeyHex.slice(-6)}`;

    return {
      address,
      balance: wallet.balance,
      funded: wallet.funded,
      active: wallet.active,
      privateKey: secretKeyHex,
      privateKeyPreview,
    };
  });
}
// Returns the full private key as a hex string for a given address, or null if not found
getBundlerPrivateKeyHex(address: string): string | null {
  // 1) Try in-memory map
  const w = this.bundlerWallets.get(address);
  if (w?.keypair?.secretKey) {
    return Array.from(w.keypair.secretKey)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  // 2) Fallback: read from localStorage
  try {
    const stored = localStorage.getItem("bundler_wallets_DEV");
    if (!stored) return null;
    const walletsData = JSON.parse(stored) as Array<{
      address: string;
      secretKey: number[];
    }>;
    const found = walletsData.find((x) => x.address === address);
    if (!found?.secretKey) return null;

    const secretKeyHex = found.secretKey
      .map((b) => Number(b).toString(16).padStart(2, "0"))
      .join("");

    return secretKeyHex;
  } catch {
    return null;
  }
}

}

export default new AdvancedToolsService();